/**
 * Cart Auto-Open — Thin Recovery Layer
 *
 * The theme's native ATC flow (interactable.min.js → T4SThemeSP.ProductAjax)
 * handles add-to-cart + section render + drawer open on all product pages.
 *
 * This script is a FALLBACK ONLY — it detects when the native flow didn't
 * open the drawer and delegates to theme APIs. It does NOT duplicate section
 * fetching or drawer rendering.
 *
 * Fallback priority:
 *   1. T4SThemeSP.Cart.getToFetch() — full theme section refresh + drawer open
 *   2. 'cart:refresh:opend' dispatch — theme Cart.init listener handles it
 *   3. Manual section fetch + drawer open (absolute last resort)
 *
 * Debug: set window.__cartAutoOpenDebug = true before ATC to see console logs.
 * Disable: set window.__skipCartAutoOpen = true to skip entirely.
 */
(function() {
  'use strict';

  if (window.__skipCartAutoOpen) return;

  window.T4S_CartAutoOpenLoaded = true;

  // Save original fetch before any wrapping
  var origFetch = window.fetch;

  // --- Debug logging ---
  function log(msg, data) {
    if (window.__cartAutoOpenDebug) {
      console.log('[cart-auto-open] ' + msg, data !== undefined ? data : '');
    }
  }

  // --- Safety polyfill ---
  // Prevents interactable.min.js crash if getToFetchSection isn't defined yet.
  // Uses origFetch (unwrapped) to avoid triggering our own intercept.
  window.T4SThemeSP = window.T4SThemeSP || {};
  if (!window.T4SThemeSP.getToFetchSection) {
    window.T4SThemeSP.getToFetchSection = function(t, e, i) {
      e = e || 'text';
      var url = t ? ((window.T4Sconfigs && T4Sconfigs.root_url) || '') + t : i;
      return origFetch(url, { method: 'GET', headers: { 'Cache-Control': 'no-cache' } })
        .then(function(res) { return res[e](); });
    };
  }

  // --- State machine ---
  var state = {
    lastAddTimestamp: 0,
    nativeHandled: false,
    fallbackInFlight: false
  };

  // --- Theme success detection ---
  function bindThemeSuccessDetection() {
    // interactable.min.js triggers jQuery 'add:cart:success' on successful ATC
    function bindJQueryListener(jq) {
      jq(document).on('add:cart:success', function() {
        state.nativeHandled = true;
        log('native theme success detected (add:cart:success)');
      });
    }

    if (window.jQuery_T4NT) {
      bindJQueryListener(jQuery_T4NT);
    } else if (window.jQuery) {
      bindJQueryListener(jQuery);
    } else {
      // Retry once jQuery is available (theme loads it deferred)
      document.addEventListener('t4s:theme:ready', function() {
        if (window.jQuery_T4NT) bindJQueryListener(jQuery_T4NT);
        else if (window.jQuery) bindJQueryListener(jQuery);
      }, { once: true });
    }

    // Watch drawer aria-hidden for opens from any source
    var drawer = document.getElementById('t4s-mini_cart');
    if (drawer && typeof MutationObserver !== 'undefined') {
      new MutationObserver(function(mutations) {
        for (var i = 0; i < mutations.length; i++) {
          if (mutations[i].attributeName === 'aria-hidden'
              && drawer.getAttribute('aria-hidden') === 'false') {
            state.nativeHandled = true;
            log('native theme success detected (drawer opened)');
          }
        }
      }).observe(drawer, { attributes: true, attributeFilter: ['aria-hidden'] });
    }
  }

  // --- Fallback check ---
  function runFallbackCheck() {
    log('fallback check started');

    if (state.nativeHandled) {
      log('fallback skipped — native handled');
      return;
    }
    if (state.fallbackInFlight) {
      log('fallback skipped — already in flight');
      return;
    }
    var drawer = document.getElementById('t4s-mini_cart');
    if (!drawer) {
      log('fallback skipped — no drawer element');
      return;
    }
    if (drawer.getAttribute('aria-hidden') === 'false') {
      log('fallback skipped — drawer already open');
      return;
    }
    if (Date.now() - state.lastAddTimestamp > 3000) {
      log('fallback skipped — stale add timestamp');
      return;
    }

    // Native theme didn't handle it — run fallback
    state.fallbackInFlight = true;
    log('fallback refresh started');

    // 1. Best: use theme Cart API (handles section fetch + render + drawer open)
    if (window.T4SThemeSP && T4SThemeSP.Cart
        && typeof T4SThemeSP.Cart.getToFetch === 'function') {
      log('using T4SThemeSP.Cart.getToFetch()');
      // Set isATCSuccess so renderContents will trigger drawer open via O()
      T4SThemeSP.isATCSuccess = true;
      T4SThemeSP.Cart.getToFetch();
      waitForDrawerOrTimeout(drawer);
      return;
    }

    // 2. Dispatch cart:refresh:opend — theme Cart.init listener handles it
    if (window.T4SThemeSP && T4SThemeSP.Cart) {
      log('dispatching cart:refresh:opend for theme Cart.init');
      document.dispatchEvent(new CustomEvent('cart:refresh:opend'));
      waitForDrawerOrTimeout(drawer);
      return;
    }

    // 3. Last resort: theme JS not loaded — manual fetch + open
    log('last resort — manual section fetch');
    manualFallback(drawer);
  }

  // Wait for drawer to open or timeout, then clean up state
  function waitForDrawerOrTimeout(drawer) {
    var resolved = false;
    function done(source) {
      if (resolved) return;
      resolved = true;
      state.fallbackInFlight = false;
      log('fallback refresh completed (' + source + ')');
      // If drawer still didn't open, force it
      if (drawer.getAttribute('aria-hidden') === 'true') {
        ensureDrawerOpen(drawer);
      }
    }
    // Listen for cart:updated as completion signal
    var onUpdated = function() {
      document.removeEventListener('cart:updated', onUpdated);
      done('cart:updated');
    };
    document.addEventListener('cart:updated', onUpdated);
    // Safety timeout
    setTimeout(function() {
      document.removeEventListener('cart:updated', onUpdated);
      done('timeout');
    }, 3000);
  }

  function ensureDrawerOpen(drawer) {
    if (drawer.getAttribute('aria-hidden') === 'false') return;
    log('drawer open invoked');
    if (window.T4SThemeSP && T4SThemeSP.Drawer
        && typeof T4SThemeSP.Drawer.opend === 'function'
        && typeof jQuery_T4NT !== 'undefined') {
      T4SThemeSP.Drawer.opend(jQuery_T4NT(drawer));
    } else {
      // Manual open — absolute last resort
      drawer.setAttribute('aria-hidden', 'false');
      var overlay = document.querySelector('.t4s-close-overlay');
      if (overlay) overlay.classList.add('is--visible');
      document.documentElement.classList.add('t4s-lock-scroll', 'is--opend-drawer');
    }
  }

  function manualFallback(drawer) {
    var rootUrl = (window.T4Sconfigs && T4Sconfigs.root_url) || '';
    origFetch(rootUrl + '/?sections=mini_cart', {
      method: 'GET',
      headers: { 'Cache-Control': 'no-cache' }
    })
    .then(function(res) { return res.json(); })
    .then(function(sections) {
      if (sections && sections.mini_cart && typeof jQuery_T4NT !== 'undefined') {
        var parsed = jQuery_T4NT(sections.mini_cart);
        var newItems = parsed.find('[data-cart-items]').html();
        var newPrices = parsed.find('[data-cart-prices]').html();
        if (newItems) jQuery_T4NT('[data-cart-items]').html(newItems);
        if (newPrices) jQuery_T4NT('[data-cart-prices]').html(newPrices);
        var ccount = parsed.attr('data-ccount')
          || parsed.find('[data-ccount]').attr('data-ccount');
        if (ccount) jQuery_T4NT('[data-cart-count]').html(ccount);
        log('manual section render completed');
      }
      document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, cancelable: true }));
      ensureDrawerOpen(drawer);
    })
    .catch(function(err) {
      log('manual fallback error', err);
      ensureDrawerOpen(drawer);
    })
    .finally(function() {
      state.fallbackInFlight = false;
      log('fallback refresh completed (manual)');
    });
  }

  // --- Fetch wrapper (tightly scoped) ---
  window.fetch = function(url, opts) {
    // Only intercept: string URL to /cart/add with POST method
    var isAtc = typeof url === 'string'
      && url.indexOf('/cart/add') !== -1
      && opts && typeof opts.method === 'string'
      && opts.method.toUpperCase() === 'POST';

    if (!isAtc) {
      return origFetch.apply(this, arguments);
    }

    log('ATC detected by fallback layer', url);

    // Reset state for this add cycle
    state.nativeHandled = false;
    state.lastAddTimestamp = Date.now();

    var args = arguments;
    var self = this;

    // Call original fetch and schedule fallback check outside the promise chain
    var resultPromise = origFetch.apply(self, args);

    // Side-effect: schedule fallback after response, without altering the chain
    resultPromise.then(function(response) {
      if (!response.ok) {
        log('ATC response not ok, skipping fallback', response.status);
        return;
      }
      // Schedule fallback check: microtask → rAF → 200ms delay
      // This gives the native theme handler time to process the response
      Promise.resolve().then(function() {
        if (typeof requestAnimationFrame === 'function') {
          requestAnimationFrame(function() {
            setTimeout(runFallbackCheck, 200);
          });
        } else {
          setTimeout(runFallbackCheck, 300);
        }
      });
    }).catch(function() {
      // Fetch failed — don't run fallback
    });

    // Return the ORIGINAL promise — no .then() wrapping that could alter timing
    return resultPromise;
  };

  // --- Init ---
  bindThemeSuccessDetection();
  log('initialized');
})();
